import { type PortfolioRow, PortfolioType } from "./types";

// Supplied fixed angles must be compatible with the selected tracking axes.
const solarTrackingFields = {
  fixed: ["tiltDeg", "orientationDeg"],
  "azimuth-tracking": ["tiltDeg"],
  "tilted-north-south-tracking": ["orientationDeg"],
  "tilted-single-axis-tracking": ["tiltDeg", "orientationDeg"],
  "full-tracking": [],
} satisfies Record<string, string[]>;

function isNumberInRange(value: unknown, min: number, max = Number.POSITIVE_INFINITY): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
}

/** Shared direct-API power configuration rules for import, Plot, and Map. Coordinates are validated separately. */
export function validateAssetPowerConfiguration(portfolioType: PortfolioType, asset: PortfolioRow): string[] {
  const fields: string[] = [];
  if (portfolioType === PortfolioType.Wind) {
    if (typeof asset.turbineId !== "string" || !/^[a-zA-Z0-9_-]+$/.test(asset.turbineId)) {
      fields.push("turbineId");
    }
    if (!isNumberInRange(asset.hubHeight, 10, 20000)) {
      fields.push("hubHeight");
    }
    return fields;
  }
  if (portfolioType !== PortfolioType.Solar) {
    return ["portfolioType"];
  }

  const specs = asset.specifications;
  // Capacity must be explicit; zero is valid and output units do not affect eligibility.
  if (!isNumberInRange(specs?.installedCapacityKw, 0)) {
    fields.push("specifications.installedCapacityKw");
  }
  const trackingType = specs?.trackingType;
  const supportedTracking = trackingType != null && Object.hasOwn(solarTrackingFields, trackingType);
  if (trackingType != null && !supportedTracking) {
    fields.push("specifications.trackingType");
  }
  // Without explicit tracking, both fixed angles may be supplied.
  const allowedAngles: readonly string[] =
    trackingType == null
      ? solarTrackingFields.fixed
      : supportedTracking
        ? solarTrackingFields[trackingType as keyof typeof solarTrackingFields]
        : [];
  for (const [field, max] of [
    ["tiltDeg", 90],
    ["orientationDeg", 360],
  ] as const) {
    const value = specs?.[field];
    if (value != null && (!isNumberInRange(value, 0, max) || !allowedAngles.includes(field))) {
      fields.push(`specifications.${field}`);
    }
  }
  if (specs?.criticalSnowdepthCm != null && !isNumberInRange(specs.criticalSnowdepthCm, 0)) {
    fields.push("specifications.criticalSnowdepthCm");
  }
  return fields;
}
